import { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, IconButton, List, ListItem, ListItemButton, ListItemText, Stack, Tooltip,
  Typography,
} from '@mui/material';
import { ArrowLeft, SquarePen, Trash2 } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { formatLocaleDateTime } from '../../../i18n/format';
import { copilotApi } from './copilotApi';

export const COPILOT_CONVERSATIONS_KEY = ['copilot', 'conversations'] as const;

/** The user's earlier chats: open one, start a new one, delete one (with a second click to confirm). */
export function CopilotHistory({ activeId, onOpen, onNew, onBack, onDeleted }: {
  activeId: string | null;
  onOpen: (id: string) => void;
  onNew: () => void;
  onBack: () => void;
  onDeleted: (id: string) => void;
}) {
  const { t, i18n } = useTranslation('copilot');
  const qc = useQueryClient();
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => { headingRef.current?.focus(); }, []);

  const list = useQuery({
    queryKey: COPILOT_CONVERSATIONS_KEY,
    queryFn: () => copilotApi.listConversations(30),
    staleTime: 15_000,
  });

  const remove = useMutation({
    mutationFn: (id: string) => copilotApi.deleteConversation(id),
    onSuccess: (_d, id) => {
      setConfirmId(null);
      onDeleted(id);
      void qc.invalidateQueries({ queryKey: COPILOT_CONVERSATIONS_KEY });
    },
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1.5, py: 1 }}>
        <Tooltip title={t('history.back')}>
          <IconButton size="small" onClick={onBack} aria-label={t('history.back')}><ArrowLeft size={16} /></IconButton>
        </Tooltip>
        <Typography ref={headingRef} tabIndex={-1} component="h2" variant="subtitle2" sx={{ flex: 1, fontWeight: 600, outline: 'none' }}>
          {t('history.title')}
        </Typography>
        <Button size="small" startIcon={<SquarePen size={14} />} onClick={onNew}>{t('actions.newChat')}</Button>
      </Stack>
      <Box sx={{ flex: 1, overflowY: 'auto', px: 0.5, pb: 1 }}>
        {list.isLoading && (
          <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress size={20} aria-label={t('history.loading')} /></Stack>
        )}
        {list.isError && <Alert severity="warning" sx={{ mx: 1 }}>{t('history.loadFailed')}</Alert>}
        {remove.isError && <Alert severity="warning" sx={{ mx: 1, mb: 1 }}>{t('history.deleteFailed')}</Alert>}
        {list.data && list.data.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4, px: 2 }}>
            {t('history.empty')}
          </Typography>
        )}
        {list.data && list.data.length > 0 && (
          <List dense disablePadding aria-label={t('history.title')}>
            {list.data.map((c) => {
              const title = c.title?.trim() || t('history.untitled');
              const when = c.lastMessageAt ?? c.createdAt;
              const confirming = confirmId === c.id;
              return (
                <ListItem
                  key={c.id}
                  disablePadding
                  secondaryAction={confirming ? (
                    <Stack direction="row" spacing={0.5}>
                      <Button size="small" color="error" onClick={() => remove.mutate(c.id)} disabled={remove.isPending}>
                        {t('history.confirmDelete')}
                      </Button>
                      <Button size="small" onClick={() => setConfirmId(null)}>{t('proposal.cancel')}</Button>
                    </Stack>
                  ) : (
                    <Tooltip title={t('history.delete')}>
                      <IconButton edge="end" size="small" onClick={() => setConfirmId(c.id)} aria-label={t('history.deleteNamed', { title })}>
                        <Trash2 size={14} />
                      </IconButton>
                    </Tooltip>
                  )}
                  sx={confirming ? { '& .MuiListItemButton-root': { pr: 20 } } : undefined}
                >
                  <ListItemButton selected={c.id === activeId} onClick={() => onOpen(c.id)} sx={{ borderRadius: 1 }}>
                    <ListItemText
                      primary={title}
                      secondary={when ? formatLocaleDateTime(when, i18n.language) : undefined}
                      primaryTypographyProps={{ noWrap: true, variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        )}
      </Box>
    </Box>
  );
}
